import { BudgetPhases, Phase } from 'business/constants';
import {
    AMOUNT_SPECIAL_AUTHORIZATION_FIELD,
    AMOUNT_AVAILABLE_FIELD,
    SUPER_OPERATION_FIELD,
    SUPER_OPERATION_NAME_FIELD,
    BUDGET_LINE_ID,
    AMOUNT_AMENDING_DECISION_FIELD,
    AMOUNT_VOTED_SUP_FIELD,
    VOTED_TOTAL_FIELD,
    PERCENT_REALIZED_FIELD,
    REQUESTED_FIELD,
    EVOLUTION_N_N_1,
    AGG_STATUS,
    REPORTED_FIELD,
    SUPER_GESTIONNAIRE_FIELD,
    SUPER_GESTIONNAIRE_CODE_FIELD,
    CHAPTER_LABEL_FIELD,
    DIRECTION_CODE,
    DIRECTION_LABEL,
} from 'routes/BudgetModule/config/constants';
import {
    BudgetProposalT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import { getInfoIconTemplate } from 'helpers/iconHelper';
import { percentageComparator } from 'ui/AgGridReact/comparators';
import { ColDef, GridApi, ValueGetterParams } from 'ag-grid-community';
import { CellStyleT } from 'ui/AgGridReact/colDefConfig';
import { percentageGetter, valueGetter } from 'ui/AgGridReact/getters';
import {
    budgetCurrencyFormatter,
    budgetPercentageFormatter,
    statusFormatter,
} from 'ui/AgGridReact/formatters';
import {
    getRealizationRateColor,
    translateMultipleName,
    getFieldByExercice,
} from 'routes/BudgetModule/helpers/format';

import {
    calculateEvolution,
    calculateEvolutionInPercentage,
} from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/gridHelpers/evolutionHelpers';
import {
    getAmountFieldForPhase,
    getFieldForPhase,
    getPreviousPhase,
    getIsEditablePhase,
    isLastActivePhase,
    isSimulatedPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import { getResourceNameField } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { doesPhaseHaveRejectedStatuses } from 'routes/BudgetModule/helpers/statusHelpers';
import { cellStyleBold } from './styles';
import {
    getAggAmountImputations,
    getAggStatusImputations,
    getAggEvolution,
    sortImputationsByStatus,
} from './aggregators';
import { getClientBind } from 'store/APIClient';

const getColumnByYear = (
    field: string,
    year: number,
    colDefParams: Partial<ColDef> = {},
): ColDef => {
    const fieldWithYear = getFieldByExercice(field, year);
    return {
        field: fieldWithYear,
        headerName: translateMultipleName(fieldWithYear),
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        floatingFilterComponent: 'numberFloatingFilter',
        enableRowGroup: true,
        aggFunc: 'sum',
        valueFormatter: budgetCurrencyFormatter,
        filterValueGetter: valueGetter,
        cellStyle: cellStyleBold,
        ...colDefParams,
    };
};

const getAmountRealizedColumnByYear = (year: number): ColDef =>
    getColumnByYear('budgetLine.amountRealized', year);
const getAmountMandatedColumnByYear = (year: number): ColDef =>
    getColumnByYear('budgetLine.amountMandated', year);
const getAmountVotedColumnByYear = (year: number): ColDef =>
    getColumnByYear('budgetLine.amountVoted', year);
const getAmountEngagedUnpaidColumnByYear = (year: number): ColDef =>
    getColumnByYear('budgetLine.amountEngagedUnpaid', year);

const getPhasedAmountColDefs = (
    phase: BudgetPhases,
    exercice: number,
    currentBudgetProposal: BudgetProposalT,
    statusesSettings: StatusesSettingsT,
    cellStyle: CellStyleT,
    t: TranslationT,
    hide: boolean = false,
): ColDef[] => {
    const column: ColDef = {
        field: getAmountFieldForPhase(phase),
        headerName: getFieldForPhase(phase, 'getPhaseHeaderName')(exercice, t),
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        floatingFilterComponent: 'numberFloatingFilter',
        enableRowGroup: true,
        aggFunc: 'sum',
        valueGetter: (params: ValueGetterParams) =>
            getAggAmountImputations(params, exercice, phase, statusesSettings),
        valueFormatter: budgetCurrencyFormatter,
        cellStyle,
        filterValueGetter: (params: ValueGetterParams) =>
            getAggAmountImputations(
                { context: params.context, data: params?.node?.data },
                exercice,
                phase,
                statusesSettings,
            ),
        hide: hide,
    };
    if (
        getIsEditablePhase(phase) &&
        doesPhaseHaveRejectedStatuses(phase, statusesSettings) &&
        // we want to display tooltip only for current phase amount column
        currentBudgetProposal.phase === phase
    ) {
        return [
            {
                headerTooltip: t('budgetProposal.arbitrageAmountTooltip'),
                ...column,
            },
        ];
    }
    return [column];
};

const getPhasedAggStatusColDefs = (
    budgetProposal: BudgetProposalT,
    // we may want to get the col def for another phase that the bp's
    statusesSettings: StatusesSettingsT,
    phasesSettings: Phase[],
    t: TranslationT,
): ColDef[] => {
    const columns: ColDef[] = [];
    columns.push({
        field: AGG_STATUS,
        headerName: t('common.status'),
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponent: 'textFloatingFilter',
        enableRowGroup: true,
        cellRenderer: 'aggStatusCellRenderer',
        maxWidth: 170,
        sort: 'asc',
        comparator: sortImputationsByStatus,
        valueGetter: (params: ValueGetterParams) =>
            getAggStatusImputations(
                params,
                budgetProposal,
                statusesSettings,
                phasesSettings,
            ),
        valueFormatter: statusFormatter,
        filterValueGetter: (params: ValueGetterParams) =>
            statusFormatter({
                value: getAggStatusImputations(
                    { context: params.context, data: params?.node?.data },
                    budgetProposal,
                    statusesSettings,
                    phasesSettings,
                ),
            }),
    });
    return columns;
};

const getLastActivePhaseColDefs = (
    budgetProposal: BudgetProposalT,
    statusesSettings: StatusesSettingsT,
    t: TranslationT,
    cellStylePrimaryBold: CellStyleT,
): ColDef[] => {
    const { exercice, phase } = budgetProposal;
    const previousPhase = getPreviousPhase(phase);
    const columns: ColDef[] = [];
    columns.push(
        ...getPhasedAmountColDefs(
            // TODO StrictNullCheck : Try to remove "!"
            previousPhase!,
            exercice,
            budgetProposal,
            statusesSettings,
            {},
            t,
        ),
    );
    columns.push({
        field: getFieldByExercice(VOTED_TOTAL_FIELD, exercice),
        headerName: t('budgetProposal.totalBudgetYear', {
            year: exercice,
        }),
        headerTooltip: t('budgetProposal.arbitrageAmountTooltip'),
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        floatingFilterComponent: 'numberFloatingFilter',
        enableRowGroup: true,
        aggFunc: 'sum',
        valueGetter: (params: ValueGetterParams) => {
            const previousAmount = getAggAmountImputations(
                { context: params.context, data: params?.node?.data },
                exercice,
                // TODO StrictNullCheck : Try to remove "!"
                previousPhase!,
                statusesSettings,
            );
            const currentAmount = getAggAmountImputations(
                { context: params.context, data: params?.node?.data },
                exercice,
                phase,
                statusesSettings,
            );
            if (previousAmount != null) {
                return currentAmount != null
                    ? previousAmount + currentAmount
                    : previousAmount;
            }
            return null;
        },
        valueFormatter: budgetCurrencyFormatter,
        cellStyle: cellStylePrimaryBold,
        filterValueGetter: valueGetter,
    });
    columns.push(getColumnByYear(AMOUNT_AMENDING_DECISION_FIELD, exercice));
    return columns;
};

export const getColDefs = (
    budgetProposal: BudgetProposalT,
    gridApi: GridApi | null,
    budgetLineVentilationFields: string[],
    primaryColor: string,
    statusesSettings: StatusesSettingsT,
    phasesSettings: Phase[],
    t: TranslationT,
    softwares = new Set<string>(),
): ColDef[] => {
    const bind = getClientBind();
    const { exercice, phase, by } = budgetProposal;
    const cellStylePrimaryBold = { color: primaryColor, fontWeight: 600 };
    const cellStyleSemiBold = { fontWeight: 510 };

    const basicTextColDef = {
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponent: 'textFloatingFilter',
        enableRowGroup: true,
        cellStyle: cellStyleSemiBold,
    };
    // expandButtonColDef enables to display button to expand a budget line and display its inscription
    // it is displayed only if no grouping is detected (otherwise, a auto column group is displayed and handles it)
    const expandButtonColDef = {
        field: getFieldByExercice(BUDGET_LINE_ID, exercice),
        headerName: '',
        cellRenderer: 'agGroupCellRenderer',
        suppressHeaderMenuButton: true,
        editable: false,
        suppressNavigable: true,
        maxWidth: 30,
        suppressColumnsToolPanel: true,
        resizable: false,
        suppressMovable: true,
        lockPosition: true,
    };
    const columns: ColDef[] = [
        expandButtonColDef,
        {
            field: getResourceNameField(by),
            sort: 'asc',
            hide: true,
            ...basicTextColDef,
        },
    ];
    columns.push({
        field: CHAPTER_LABEL_FIELD,
        sort: 'asc',
        hide: true,
        ...basicTextColDef,
    });

    if (softwares.has('CIRIL_FINANCES')) {
        columns.push({
            field: SUPER_GESTIONNAIRE_FIELD,
            sort: 'asc',
            hide: true,
            ...basicTextColDef,
        });
        columns.push({
            field: SUPER_GESTIONNAIRE_CODE_FIELD,
            sort: 'asc',
            hide: true,
            ...basicTextColDef,
        });
    }

    if (softwares.has('SEDIT_FINANCES')) {
        columns.push({
            field: DIRECTION_CODE,
            sort: 'asc',
            hide: true,
            ...basicTextColDef,
        });
        columns.push({
            field: DIRECTION_LABEL,
            sort: 'asc',
            hide: true,
            ...basicTextColDef,
        });
    }

    columns.push(
        ...getPhasedAggStatusColDefs(
            budgetProposal,
            statusesSettings,
            phasesSettings,
            t,
        ),
    );

    columns.push(
        ...getPhasedAmountColDefs(
            phase,
            exercice,
            budgetProposal,
            statusesSettings,
            cellStylePrimaryBold,
            t,
        ),
    );

    columns.push(getColumnByYear(AMOUNT_SPECIAL_AUTHORIZATION_FIELD, exercice));

    // For simulated phase (arbitrage) we want to add the amount column from service proposals
    if (isSimulatedPhase(phase) && getPreviousPhase(phase))
        columns.push(
            ...getPhasedAmountColDefs(
                // TODO StrictNullCheck : Try to remove "!"
                getPreviousPhase(phase)!,
                exercice,
                budgetProposal,
                statusesSettings,
                {},
                t,
            ),
        );
    // For last phase (supplementary budget) we add primitive, evolution and total budget columns
    if (isLastActivePhase(phase))
        columns.push(
            ...getLastActivePhaseColDefs(
                budgetProposal,
                statusesSettings,
                t,
                cellStylePrimaryBold,
            ),
        );
    // (Shortcut ID : 30915) : add amounts for ampmetropole
    else if (bind === 'ampmetropole') {
        [BudgetPhases.VOTE, BudgetPhases.SUPPLEMENTARY_BUDGET].forEach((p) =>
            columns.push(
                ...getPhasedAmountColDefs(
                    // TODO StrictNullCheck : Try to remove "!"
                    p,
                    exercice,
                    budgetProposal,
                    statusesSettings,
                    {},
                    t,
                    true,
                ),
            ),
        );
    }

    columns.push(getColumnByYear(REPORTED_FIELD, exercice));
    columns.push(getAmountMandatedColumnByYear(exercice));

    columns.push(getAmountRealizedColumnByYear(exercice - 3));
    columns.push(getAmountMandatedColumnByYear(exercice - 3));
    columns.push(getAmountEngagedUnpaidColumnByYear(exercice - 3));

    columns.push(getAmountVotedColumnByYear(exercice - 2));

    columns.push(getAmountVotedColumnByYear(exercice - 3));

    columns.push(getAmountRealizedColumnByYear(exercice - 2));
    columns.push(getAmountEngagedUnpaidColumnByYear(exercice - 2));
    columns.push(getAmountMandatedColumnByYear(exercice - 2));

    columns.push(getAmountVotedColumnByYear(exercice - 1));
    columns.push(getAmountMandatedColumnByYear(exercice - 1));

    columns.push(getAmountRealizedColumnByYear(exercice - 1));
    columns.push(getAmountEngagedUnpaidColumnByYear(exercice - 1));

    columns.push(
        getColumnByYear(AMOUNT_SPECIAL_AUTHORIZATION_FIELD, exercice - 1, {
            hide: true,
        }),
    );
    columns.push(
        getColumnByYear(AMOUNT_AMENDING_DECISION_FIELD, exercice - 1, {
            hide: true,
        }),
    );
    columns.push(
        getColumnByYear(AMOUNT_VOTED_SUP_FIELD, exercice - 1, {
            hide: true,
        }),
    );

    if (bind === 'ampmetropole') {
        columns.push(
            getColumnByYear(AMOUNT_AMENDING_DECISION_FIELD, exercice, {
                hide: true,
            }),
        );
    }

    columns.push(getAmountRealizedColumnByYear(exercice));
    columns.push(getAmountEngagedUnpaidColumnByYear(exercice));
    columns.push(getColumnByYear(AMOUNT_AVAILABLE_FIELD, exercice));

    const columnsWithExercice: ColDef[] = [
        {
            field: getFieldByExercice(PERCENT_REALIZED_FIELD, exercice - 1),
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetPercentageFormatter,
            filterValueGetter: percentageGetter,
            cellRenderer: 'percentRealizedCellRenderer',
            cellRendererParams: {
                budgetProposal,
                getColor: (value: number) =>
                    getRealizationRateColor(value, true),
            },
        },
        {
            field: getFieldByExercice(VOTED_TOTAL_FIELD, exercice - 1),
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            floatingFilterComponent: 'numberFloatingFilter',
            aggFunc: 'sum',
            valueFormatter: budgetCurrencyFormatter,
            filterValueGetter: valueGetter,
        },
        {
            field: getFieldByExercice(PERCENT_REALIZED_FIELD, exercice),
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            floatingFilterComponent: 'numberFloatingFilter',
            valueFormatter: budgetPercentageFormatter,
            filterValueGetter: percentageGetter,
            cellRenderer: 'percentRealizedCellRenderer',
            cellRendererParams: {
                budgetProposal,
                getColor: (value: number) =>
                    getRealizationRateColor(value, true),
            },
        },
    ];

    columnsWithExercice.push({
        field: getFieldByExercice(EVOLUTION_N_N_1, exercice),
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        floatingFilterComponent: 'numberFloatingFilter',
        comparator: percentageComparator,
        headerComponentParams: { template: getInfoIconTemplate(primaryColor) },
        headerTooltip: t('budgetProposal.evolutionHeaderTooltip'),

        valueGetter: (params: ValueGetterParams) =>
            getAggEvolution(
                params,
                gridApi,
                budgetProposal,
                calculateEvolutionInPercentage,
                t,
            ),

        filterValueGetter: (params: ValueGetterParams) => {
            const evolution = getAggEvolution(
                params,
                gridApi,
                budgetProposal,
                calculateEvolution,
                t,
            );
            if (evolution === null) return null;

            return 100 * evolution;
        },
    });

    columnsWithExercice.push({
        field: getFieldByExercice(REQUESTED_FIELD, exercice),
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        floatingFilterComponent: 'numberFloatingFilter',
        aggFunc: 'sum',
        valueFormatter: budgetCurrencyFormatter,
        filterValueGetter: valueGetter,
    });

    const hiddenByDefaultFields: string[] = [
        SUPER_OPERATION_NAME_FIELD,
        SUPER_OPERATION_FIELD,
    ];

    budgetLineVentilationFields.forEach((field) => {
        if (columns.some((col) => col.field === field)) {
            return; // if the column already exists to avoid duplicates.
        }
        columns.push({
            field,
            hide: hiddenByDefaultFields.includes(field),
            ...basicTextColDef,
        });
    });

    columns.push(
        ...columnsWithExercice.map((col) => ({
            ...basicTextColDef,
            ...col,
            headerName: translateMultipleName(col.field || ''),
        })),
    );

    return columns;
};
